// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module CohortTable
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS cohort-retention table for comparing
 * cohort sizes, retention checkpoints, and last activity across segments.
 */

import type { CohortAnalysis, CohortBucket } from "@geenius/analytics";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
} from "@geenius/ui/solidjs-css";
import { type Component, For, Show } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the SolidJS CSS cohort retention table.
 *
 * @property analysis Cohort analysis payload produced by shared helpers.
 * @property title Optional section title override.
 * @property maxRows Maximum cohort rows to render.
 * @property isLoading Whether the table should render placeholder rows.
 * @property error Optional error state rendered with retry recovery.
 * @property onRetry Optional retry callback for error states.
 * @property class Optional class name merged into the card wrapper.
 */
export interface CohortTableProps {
  analysis: CohortAnalysis | null;
  title?: string;
  maxRows?: number;
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

function getRetentionPoint(cohort: CohortBucket, day: number) {
  return cohort.retained.find((point) => point.day === day);
}

/**
 * Renders a cohort retention table for the SolidJS CSS analytics variant.
 *
 * @param props Cohort analysis data and optional loading/error controls.
 * @returns A card-framed cohort table with accessible row and column labels.
 */
export const CohortTable: Component<CohortTableProps> = (props) => {
  const copy = createAnalyticsCopy();
  const intervals = () => props.analysis?.intervals ?? [];
  const cohorts = () =>
    (props.analysis?.cohorts ?? []).slice(0, props.maxRows ?? 6);
  const tableTitle = () =>
    props.title ??
    props.analysis?.name ??
    copy.t("analytics.dashboard.cohorts");

  return (
    <Card
      class={cx("gn-analytics-cohort-table", props.class)}
      data-analytics-cohort-table
    >
      <CardHeader class="pb-0">
        <CardTitle class="text-sm" data-analytics-section-title>
          {tableTitle()}
        </CardTitle>
      </CardHeader>
      <CardContent class="pt-4">
        <Show
          when={!props.error}
          fallback={<ErrorState error={props.error} onRetry={props.onRetry} />}
        >
          <Show
            when={!props.isLoading}
            fallback={
              <div
                class="grid gap-3"
                data-analytics-cohort-table-loading
                aria-label={copy.t("analytics.topPages.loading")}
              >
                <Skeleton class="h-6 w-full" />
                <Skeleton class="h-6 w-full" />
                <Skeleton class="h-6 w-full" />
              </div>
            }
          >
            <Show
              when={cohorts().length > 0}
              fallback={
                <EmptyState
                  bordered={false}
                  compact
                  title={copy.t("analytics.dashboard.noCohorts")}
                />
              }
            >
              <div class="overflow-x-auto" data-analytics-cohort-table-viewport>
                <table
                  class="w-full"
                  data-analytics-table
                  aria-label={tableTitle()}
                >
                  <thead>
                    <tr>
                      <th scope="col">
                        {copy.t("analytics.cohorts.columns.cohort")}
                      </th>
                      <th scope="col">
                        {copy.t("analytics.cohorts.columns.size")}
                      </th>
                      <For each={intervals()}>
                        {(day) => (
                          <th scope="col">
                            {copy.t("analytics.dashboardBuilder.dayLabel", {
                              day,
                            })}
                          </th>
                        )}
                      </For>
                      <th scope="col">
                        {copy.t("analytics.cohorts.columns.lastActivity")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={cohorts()}>
                      {(cohort) => (
                        <tr data-analytics-cohort-row>
                          <th
                            class="p-2 text-left align-middle font-medium text-foreground"
                            scope="row"
                          >
                            {cohort.cohort}
                          </th>
                          <td>
                            {copy.plural(
                              "analytics.dashboardBuilder.users",
                              cohort.size,
                            )}
                          </td>
                          <For each={intervals()}>
                            {(day) => {
                              const point = getRetentionPoint(cohort, day);
                              return (
                                <td>
                                  <span
                                    class="font-medium text-foreground"
                                    data-analytics-cohort-retention-rate
                                  >
                                    {copy.formatPercent(
                                      point?.retentionRate ?? 0,
                                    )}
                                  </span>
                                  <span
                                    class="ml-2 text-xs text-muted-foreground"
                                    data-analytics-cohort-retained-users
                                  >
                                    {copy.formatNumber(
                                      point?.retainedUsers ?? 0,
                                    )}
                                  </span>
                                </td>
                              );
                            }}
                          </For>
                          <td>{copy.formatTime(cohort.lastActivity)}</td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Show>
          </Show>
        </Show>
      </CardContent>
    </Card>
  );
};
